import { COMPONENT_COLOR_THEMES, COMPONENT_VALUE_TYPES as types, APP_HANDLERS } from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

export type ColorTheme = 'LIGHT' | 'DARK';

export interface ItemProps {
  name: string;
  content: string;
}

export interface BlockProps {
  name: string;
  items: ItemProps[];
}

export interface ButtonTextProps {
  text: string;
  link: string;
  appHandlerKey?: string;
}

export interface Props {
  title: string;
  blocks: BlockProps[];
  image?: string;
  colorTheme: ColorTheme;
  buttonText: ButtonTextProps;
}

export const propsSchema: PropsSchemaList = [
  {
    key: 'title',
    label: 'Title',
    type: types.TEXT,
  },
  {
    key: 'image',
    label: 'Image',
    type: types.S3_ASSET_URL,
  },
  {
    key: 'blocks',
    label: 'Blocks',
    type: types.ARRAY,
    children: [
      {
        key: 'name',
        label: 'Name',
        type: types.TEXT,
      },
      {
        key: 'items',
        label: 'Items',
        type: types.ARRAY,
        children: [
          {
            key: 'name',
            label: 'Name',
            type: types.TEXT,
          },
          {
            key: 'content',
            label: 'Content',
            type: types.TEXT,
          },
        ]
      }
    ],
  },
  {
    key: 'buttonText',
    label: 'Button Text (Mobile Only)',
    type: types.OBJECT,
    children: [
      {
        key: 'text',
        label: 'Text',
        type: types.TEXT,
      },
      {
        key: 'link',
        label: 'Link',
        type: types.TEXT,
      },
      {
        key: 'appHandlerKey',
        label: 'Use App Handler',
        type: types.ENUM,
        options: APP_HANDLERS,
      },
    ],
  },
  {
    key: 'colorTheme',
    label: 'Color Theme',
    type: types.ENUM,
    tooltip: 'Changes the background color and text color',
    options: COMPONENT_COLOR_THEMES,
  },
];

const defaultProps: Props = {
  title: 'FF 91 Specs',
  image: 'https://genesis-cdn.ff.com/assets/images/ff_ig_dtla-02-1-default.jpg',
  blocks: [
    {
      name: 'Power and Drivetrain',
      items: [
        {
          name: 'Motor',
          content: 'Tri Motor'
        },
        {
          name: 'Max Horsepower',
          content: '1,050HP'
        },
        {
          name: 'Torque',
          content: '457 lb.-ft per motor'
        },
        {
          name: 'Drive Type',
          content: 'Full-time, all-wheel drive'
        },
        {
          name: '0 to 60 mph',
          content: '2.1 Seconds'
        },
        {
          name: 'Quarter Mile',
          content: '100 miles'
        },
        {
          name: 'Top Speed',
          content: '200 MPH'
        },
      ]
    },
    {
      name: 'Charging',
      items: [
        {
          name: 'Compatibility',
          content: 'All Level II and Level III systems',
        },
        {
          name: 'Charging Standard',
          content: 'Combined Charging System(CCS)',
        },
        {
          name: 'Fast Charging Capability',
          content: '200 kW peak charging power',
        },
        {
          name: 'AC Charging',
          content: 'Up to XX.X kW',
        },
        {
          name: 'Charging Speed',
          content: 'Up to XXXX mph peak charging speed',
        },
        {
          name: 'Charging Rate',
          content: 'Up to 80% charge in 20 minutes',
        },
        {
          name: 'Onboard Charger',
          content: 'Example(Multi-functional, bi-directional(V2X) capability)',
        },
      ],
    },
    {
      name: 'Chassis',
      items: [
        {
          name: 'Wheels',
          content: '22" Aluminum Alloy Wheels,',
        },
        {
          name: 'Suspension',
          content: 'Semi-Active Dampers',
        },
        {
          name: 'Electronic Stability Control',
          content: 'Standard',
        },
        {
          name: 'Torque Vectoring',
          content: 'Standard',
        },
        {
          name: 'Parking Brake',
          content: 'Integrated EPB',
        },
        {
          name: 'Tires',
          content: 'Tire specs needed',
        },
      ],
    },
    {
      name: 'Efficiency',
      items: [
        {
          name: 'Official EPA range¹',
          content: 'XXX miles',
        },
        {
          name: 'CO2 Emissions',
          content: '0 g per mile',
        },
        {
          name: 'Aerodynamic Cd',
          content: '0.XXXXX ',
        },
      ],
    },
    {
      name: 'Dimensions',
      items: [
        {
          name: 'Width w/o mirrors',
          content: '79 inches, 2006 mm',
        },
        {
          name: 'Width with mirrors',
          content: '89.9 inches, 2283 mm ',
        },
        {
          name: 'Length',
          content: '206.7 inches, 5250 mm ',
        },
        {
          name: 'Height',
          content: '63.1 inches, 1602 mm',
        },
        {
          name: 'Wheelbase',
          content: '126 inches, 3200 mm',
        },
        {
          name: 'Weight',
          content: '6173 lbs.',
        },
      ],
    },
    {
      name: 'Volume',
      items: [
        {
          name: 'Wheels',
          content: '22" Aluminum Alloy Wheels,',
        },
        {
          name: 'Suspension',
          content: 'Semi-Active Dampers',
        },
        {
          name: 'Electronic Stability Control',
          content: 'Standard',
        },
        {
          name: 'Torque Vectoring',
          content: 'Standard',
        },
        {
          name: 'Parking Brake',
          content: 'Integrated EPB',
        },
        {
          name: 'Tires',
          content: 'Tire specs needed',
        },
      ],
    }
  ],
  buttonText: {
    text: 'Preorder Now',
    link: 'https://www.ff.com/us/preorder',
  },
  colorTheme: 'DARK'
}

export default {
  title: 'Specs Sheet',
  id: 'SpecsSheet',
  propsSchema,
  defaultProps,
}
